import {
  RocketIcon,
  MessageSquareIcon,
  ZapIcon,
  SproutIcon,
  FlowerIcon
} from 'lucide-react'

import { Separator } from '@/components/ui/separator'
import Header from '@/components/shadcn-studio/blocks/hero-section-40/header'
import HeroSection from '@/components/shadcn-studio/blocks/hero-section-40/hero-section-40'
import FeaturesSection02 from '@/components/shadcn-studio/blocks/features-section-02/features-section-02'
import FeaturesSection20 from '@/components/shadcn-studio/blocks/features-section-20/features-section-20'
import FeaturesSection10 from '@/components/shadcn-studio/blocks/features-section-10/features-section-10'
import FeaturesSection12 from '@/components/shadcn-studio/blocks/features-section-12/features-section-12'
import FAQComponent from '@/components/shadcn-studio/blocks/faq-component-16/faq-component-16'
import Pricing from '@/components/shadcn-studio/blocks/pricing-component-04/pricing-component-04'
import Footer from '@/components/shadcn-studio/blocks/footer-component-01/footer-component-01'

import type { Navigation } from '@/components/shadcn-studio/blocks/hero-section-40/hero-navigation'

// Navigation data for the header
const navigationData: Navigation[] = [
  { title: 'Features', href: '#features' },
  { title: 'How it works', href: '#how-it-works' },
  { title: 'Use Cases', href: '#use-cases' },
  { title: 'Pricing', href: '#pricing' },
  { title: 'FAQ', href: '#faq' }
]

// Features for features-section-20 (How it works - Time to value)
const featuresSection20Data = [
  { title: 'Effortless onboarding', description: 'We set up your TAM, score all your accounts, overlay signals, build sequences, and import pipeline for you on day 1.', icon: RocketIcon },
  { title: 'Value in minutes, not months', description: 'Within minutes, ProspectHunter is generating new meetings and progressing pipeline.', icon: ZapIcon },
  { title: 'Personalize your approach', description: 'Outreach with context-aware engagement messages', icon: MessageSquareIcon }
]

// FAQ items
const faqItems = [
  {
    icon: 'PackageIcon',
    title: 'What does your company do?',
    description:
      'Our company specializes in delivering high-quality solutions that are tailored to meet the evolving needs of businesses and individuals. Whether it\'s a digital product, a creative service, or a custom solution, we focus on combining functionality with design to help our clients grow, improve efficiency, and connect better with their audience.'
  },
  {
    icon: 'DollarSignIcon',
    title: 'What is your pricing structure?',
    description:
      'We offer flexible pricing plans designed to scale with your needs. From free starter plans for individuals to enterprise packages for large teams, you only pay for what you use.'
  },
  {
    icon: 'MonitorIcon',
    title: 'What is your cancellation policy?',
    description:
      'You can cancel your subscription at any time with no penalties. Your access continues until the end of the current billing period.'
  },
  {
    icon: 'HeadphonesIcon',
    title: 'Do you offer ongoing support or maintenance?',
    description:
      'Yes, we provide 24/7 customer support along with regular maintenance updates to ensure everything runs smoothly.'
  },
  {
    icon: 'HomeIcon',
    title: 'Do you offer custom solutions or packages?',
    description:
      'Absolutely! We work closely with clients to create tailored solutions that meet their specific requirements and goals.'
  },
  {
    icon: 'WrenchIcon',
    title: 'How do I get started?',
    description:
      'Simply sign up for an account, choose your plan, and you can start using the platform immediately. Our onboarding guide will walk you through every step.'
  }
]

// Pricing plans data
const pricingPlans = [
  {
    icon: SproutIcon,
    title: 'Essential Plan',
    description: 'Ideal for startups and small teams',
    price: 5,
    period: '/month',
    buttonText: 'Basic access',
    features: [
      '1 user account',
      'Up to 30 monthly transactions',
      '10 crypto pairs',
      'Basic market analysis',
      'Integrated wallet API'
    ]
  },
  {
    icon: FlowerIcon,
    title: 'Advanced Plan',
    description: 'Designed for teams and businesses.',
    price: 49,
    period: '/month',
    buttonText: 'Premium Access',
    features: [
      '5 user account',
      'Up to 100 monthly transactions',
      '100 crypto pairs',
      'Advance market analysis'
    ],
    extraFeatures: [
      '24/7 customer support',
      'User-friendly mobile app',
      'Strong data encryption'
    ],
    isPopular: true
  }
]

export default function LandingPage() {
  return (
    <div className='min-h-screen bg-background'>
      {/* Header / Navigation */}
      <Header navigationData={navigationData} />

      {/* Hero Section */}
      <HeroSection />

      {/* Features Section 02 - Build, Brand, and Launch (accordion with image) */}
      <div id='features'>
        <FeaturesSection02 />
      </div>

      {/* Separator */}
      <Separator className='mx-auto max-w-7xl' />

      {/* Features Section 20 - Time to value */}
      <div id='how-it-works'>
        <FeaturesSection20 features={featuresSection20Data} />
      </div>

      {/* Separator */}
      <Separator className='mx-auto max-w-7xl' />

      {/* Separator */}
      <Separator className='mx-auto max-w-7xl' />

      {/* Features Section 10 - For every role (Use Cases) */}
      <div id='use-cases'>
        <FeaturesSection10 />
      </div>

      {/* Features Section 12 - Developed from scratch */}
      <FeaturesSection12 />

      {/* FAQ Section */}
      <div id='faq'>
        <FAQComponent faqItems={faqItems} />
      </div>

      {/* Pricing Section */}
      <div id='pricing'>
        <Pricing plans={pricingPlans} />
      </div>

      {/* Footer */}
      <Footer />
    </div>
  )
}
